import { createStore } from '@oneappexchange/lwc-wired';
import { decodeHTML } from 'entities';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { cleanExcerpt } from '~/modules/utilities/string_utils';
import {
    GetPostContentOptions,
    WordPressContentType,
    WordPressPersona,
    WordPressPostData,
    WordPressService,
} from '../services/wordpress_service';

const wordPressService = new WordPressService();

// This represents an individual "post" or individual learn page. For example /learn/how-to/random-slug is one of these
export type ContentItemState = {
    isLoading: boolean;
    isError: boolean;
    data?: WordPressPostData;
};

// This is used for the Learn Home Page and represents the order and current list by contentType (or Persona) but the
// data is stored in contentItemsByContentType.
export type OrderedContentItems = {
    name: string;
    slug: string;
    singular: string;
    plural: string;
    totalItems: number;
    // These are just the slugs. contentItemData will store the data.
    orderedItems?: string[];
    isLoading: boolean;
    isError: boolean;
};

export type HydratedContentItems = {
    name: string;
    slug: string;
    singular: string;
    plural: string;
    totalItems: number;
    hydratedItems: ContentItemState[];
    isLoading: boolean;
    isError: boolean;
};

export interface WordPressState extends State {
    contentItemData: Record<string, ContentItemState>;
    orderedItemsByFilterKey: Record<string, OrderedContentItems>;
    contentTypes: WordPressContentType[];
    contentTypesOverallError: boolean;
    personas: WordPressPersona[];
    personasOverallError: boolean;
    currentSlug?: string;
    currentOptions: GetPostContentOptions;
    currentLanguage?: string;
}

const initialState: WordPressState = {
    contentItemData: {},
    orderedItemsByFilterKey: {},
    contentTypes: [],
    contentTypesOverallError: false,
    personas: [],
    personasOverallError: false,
    currentSlug: undefined,
    currentOptions: {},
    currentLanguage: undefined,
};

export const generateFilterKey = (contentType?: string, persona?: string) => {
    if (!contentType && !persona) {
        return undefined;
    }
    const key: string[] = [];
    if (contentType) {
        key.push(`contentType=${contentType}`);
    }
    if (persona) {
        key.push(`persona=${persona}`);
    }
    return key.join(',');
};

export const selectContentTypes = (state: WordPressState) => {
    return state.contentTypes;
};

export const selectPersonas = (state: WordPressState) => {
    return state.personas;
};

const selectContentItemBySlug = (state: WordPressState, slug?: string, section?: string) => {
    if (!slug) {
        return undefined;
    }
    return state.contentItemData[generateCacheKey(slug, state, section)];
};

export const selectCurrentContentItem = (state: WordPressState) => {
    return selectContentItemBySlug(state, state.currentSlug);
};

export const selectSlugContent = (state: WordPressState, slug: string, section?: string) => {
    return selectContentItemBySlug(state, slug, section);
};
export const selectPreview = (state: WordPressState) => {
    return state.currentOptions.previewToken;
};

export const selectHydratedContentItemsByContentType = (state: WordPressState): HydratedContentItems[] =>
    state.contentTypes.reduce((accumulator, contentTypeObj) => {
        const key = generateFilterKey(contentTypeObj.slug);
        const contentType = key ? state.orderedItemsByFilterKey[key] : undefined;
        if (contentType) {
            const hydrated: HydratedContentItems = {
                name: contentType.name,
                slug: contentType.slug,
                singular: contentType.singular,
                plural: contentType.plural,
                totalItems: contentType.totalItems,
                hydratedItems: [],
                isLoading: contentType.isLoading,
                isError: contentType.isError,
            };

            contentType.orderedItems?.forEach((slug: string) => {
                const cacheKey = generateCacheKey(slug, state);
                if (state.contentItemData[cacheKey]) {
                    hydrated.hydratedItems.push(state.contentItemData[cacheKey]);
                }
            });

            accumulator.push(hydrated);
        }
        return accumulator;
    }, [] as HydratedContentItems[]);

export const selectHydratedContentItems = (
    state: WordPressState,
    contentType?: string,
    persona?: string,
): HydratedContentItems | undefined => {
    const key = generateFilterKey(contentType, persona);
    if (!key) {
        return undefined;
    }
    const dehydrated = state.orderedItemsByFilterKey[key];
    if (!dehydrated) {
        return undefined;
    }

    const hydrated: HydratedContentItems = {
        name: dehydrated.name,
        slug: dehydrated.slug,
        singular: dehydrated.singular,
        plural: dehydrated.plural,
        totalItems: dehydrated.totalItems,
        hydratedItems: [],
        isLoading: dehydrated.isLoading,
        isError: dehydrated.isError,
    };
    dehydrated.orderedItems?.forEach((slug: string) => {
        const cacheKey = generateCacheKey(slug, state);
        if (state.contentItemData[cacheKey]) {
            hydrated.hydratedItems.push(state.contentItemData[cacheKey]);
        }
    });
    return hydrated;
};

function decodeHtmlReference(value: string): string;
function decodeHtmlReference(value: undefined): undefined;
function decodeHtmlReference(value: string | undefined): string | undefined;
function decodeHtmlReference(value: string | undefined) {
    return value === undefined ? undefined : decodeHTML(value);
}

const decodeYoastHeadJson = (
    yoastHeadJson: NonNullable<WordPressPostData['yoast_head_json']>,
): NonNullable<WordPressPostData['yoast_head_json']> => ({
    ...yoastHeadJson,
    title: decodeHtmlReference(yoastHeadJson.title),
    description: decodeHtmlReference(yoastHeadJson.description),
    author: decodeHtmlReference(yoastHeadJson.author),
    og_title: decodeHtmlReference(yoastHeadJson.og_title),
    og_description: decodeHtmlReference(yoastHeadJson.og_description),
    og_url: decodeHtmlReference(yoastHeadJson.og_url),
    og_site_name: decodeHtmlReference(yoastHeadJson.og_site_name),
    og_image: yoastHeadJson.og_image?.map((image) => ({
        ...image,
        url: decodeHtmlReference(image.url),
    })),
    article_published_time: decodeHtmlReference(yoastHeadJson.article_published_time),
    article_modified_time: decodeHtmlReference(yoastHeadJson.article_modified_time),
    twitter_creator: decodeHtmlReference(yoastHeadJson.twitter_creator),
    twitter_site: decodeHtmlReference(yoastHeadJson.twitter_site),
    twitter_misc: yoastHeadJson.twitter_misc
        ? Object.fromEntries(Object.entries(yoastHeadJson.twitter_misc).map(([key, value]) => [key, decodeHTML(value)]))
        : yoastHeadJson.twitter_misc,
});

// This purposely leaves out a lot of extra properties returned by the WordPress API to just the ones we care about
const trimAndCleanContentItem = (item: WordPressPostData): WordPressPostData => ({
    id: item.id,
    date: item.date,
    slug: item.slug,
    status: item.status,
    content: item.content,
    excerpt: item.excerpt?.rendered ? { rendered: cleanExcerpt(item.excerpt.rendered) } : item.excerpt,
    featured_media_url: item.featured_media_url,
    title: item.title?.rendered ? { rendered: decodeHTML(item.title.rendered) } : item.title,
    link: item.link,
    yoast_head_json: item.yoast_head_json ? decodeYoastHeadJson(item.yoast_head_json) : undefined,
    authors: item.authors,
    read_time: item.read_time,
    content_type: item.content_type,
});

// Helper function for adjusting the post requests to always get from the 0th post to page * pageSize posts, but
// minimizing the number of posts fetched to only those required.
export const getActualFetchSize = (currentPosts: OrderedContentItems, page: number, pageSize: number) => {
    if (!currentPosts?.orderedItems?.length) {
        // If none are loaded fetch from the first post to all the expected posts on this page
        return { page: 1, pageSize: page * pageSize };
    }

    const loadedPosts = currentPosts.orderedItems.length;
    const expectedExistingPosts = (page - 1) * pageSize;
    if (loadedPosts < expectedExistingPosts) {
        // If not enough posts are loaded for the next page (for example we've jumped from the initial 3 to 12), we need
        // to fetch the missing ones
        return { page: 1, pageSize: expectedExistingPosts + pageSize };
    }

    // Finally we can assume we just need the next set
    return { page, pageSize };
};

// For use on the client side when we'd want multiple components to be able to import the same instance
export const wordPressStore = createWordPressStore();

export function createWordPressStore() {
    return createStore(
        initialState,
        (set: SetState<WordPressState>, get: GetState<WordPressState>) => ({
            setLanguage(language: string) {
                set(function updateLanguage(draft: WordPressState) {
                    draft.currentOptions.language = language;
                });
            },
            setPreviewId(previewToken?: string) {
                set(function updatePreviewId(draft: WordPressState) {
                    draft.currentOptions.previewToken = previewToken;
                });
            },
            async updateSlug(slug: string, options?: GetPostContentOptions, serviceUrl?: string) {
                if (!slug) {
                    return;
                }

                const currentState = get();

                // If we have a current slug, and content keys are the same, don't re-fetch the content
                if (currentState.currentSlug) {
                    const nextContentKey = `${options?.wordPressQueryType ?? 'post'}:${slug}`;
                    const currentContentKey = generateCacheKey(currentState.currentSlug, currentState);
                    if (currentContentKey === nextContentKey) {
                        return;
                    }
                }

                set(function updateCurrentParams(draft: WordPressState) {
                    draft.currentSlug = slug;
                    if (options) {
                        draft.currentOptions = options;
                    }
                });
                await this.fetchPostCurrentContent(serviceUrl);
            },
            async fetchContentTypes() {
                const currentState = get();
                // Only fetch if we don't have them cached already
                if (currentState.contentTypes.length === 0) {
                    try {
                        const contentTypes = await wordPressService.getContentTypes(
                            currentState.currentOptions.language,
                        );
                        set(function updateContentTypes(draft: WordPressState) {
                            draft.contentTypes = contentTypes;
                            contentTypes.forEach((contentType) => {
                                const key = generateFilterKey(contentType.slug);
                                if (key) {
                                    draft.orderedItemsByFilterKey[key] = {
                                        name: contentType.name,
                                        slug: contentType.slug,
                                        singular: contentType?.labels?.singular || contentType.name,
                                        plural: contentType?.labels?.plural || contentType.name,
                                        totalItems: contentType.count,
                                        orderedItems: [],
                                        isLoading: false,
                                        isError: false,
                                    };
                                }
                            });
                            draft.contentTypesOverallError = false;
                        });
                    } catch (err) {
                        set(function updateForContentTypesError(draft: WordPressState) {
                            draft.contentTypesOverallError = true;
                        });
                    }
                }
            },
            // Warning: Page number is 1-indexed, not 0-indexed!
            async fetchPostsWithFilter(
                page: number,
                pageSize: number,
                contentType?: string,
                persona?: string,
                language?: string,
                learnLanding?: boolean,
            ) {
                const key = generateFilterKey(contentType, persona);
                if (!key) {
                    return;
                }

                const currentState = get();
                const currentPosts: OrderedContentItems = currentState.orderedItemsByFilterKey[key] || {
                    name: '',
                    slug: key,
                    singular: '',
                    plural: '',
                    totalItems: -1,
                    isLoading: false,
                    isError: false,
                };
                let shouldFetch = !currentPosts.orderedItems;
                // Check if we already have enough info or there is more to fetch (don't fetch if we're already loading)
                if (currentPosts.orderedItems && currentPosts.orderedItems.length < currentPosts.totalItems) {
                    const targetCount = page * pageSize;
                    shouldFetch = targetCount > currentPosts.orderedItems.length;
                }
                shouldFetch = shouldFetch && !currentPosts.isLoading && !currentPosts.isError;

                if (shouldFetch) {
                    set(function updateContentTypeForLoading(draft: WordPressState) {
                        const loadingStatus: OrderedContentItems = {
                            ...currentPosts,
                            isLoading: true,
                            isError: false,
                        };
                        draft.orderedItemsByFilterKey[key] = loadingStatus;
                    });

                    try {
                        const adjustedPages = getActualFetchSize(currentPosts, page, pageSize);
                        const response = await wordPressService.getFilteredPosts(
                            adjustedPages.page,
                            adjustedPages.pageSize,
                            contentType,
                            persona,
                            language,
                            learnLanding,
                        );
                        set(function updateContentType(draft: WordPressState) {
                            const updatedPosts: OrderedContentItems = {
                                ...currentPosts,
                                totalItems: response.totalItems,
                                isLoading: false,
                                isError: false,
                            };

                            if (!updatedPosts.orderedItems) {
                                updatedPosts.orderedItems = [];
                            }

                            for (const post of response.posts) {
                                if (!updatedPosts.orderedItems.includes(post.slug)) {
                                    updatedPosts.orderedItems.push(post.slug);
                                    draft.contentItemData[generateCacheKey(post.slug, currentState)] = {
                                        isLoading: false,
                                        isError: false,
                                        data: trimAndCleanContentItem(post),
                                    };
                                }
                            }

                            draft.orderedItemsByFilterKey[key] = updatedPosts;
                        });
                    } catch (err) {
                        set(function updateContentTypeForError(draft: WordPressState) {
                            draft.orderedItemsByFilterKey[key] = {
                                ...currentPosts,
                                isLoading: false,
                                isError: true,
                            };
                        });
                    }
                }
            },
            async fetchPostCurrentContent(serviceUrl?: string) {
                const currentState = get();
                const { currentSlug, currentOptions } = currentState;
                return this.fetchPostContentForSlug(currentSlug, currentOptions, undefined, serviceUrl);
            },
            async fetchSlugContent(slug: string, section?: string) {
                const currentState = get();
                return this.fetchPostContentForSlug(slug, currentState.currentOptions, section);
            },

            async fetchPostContentForSlug(
                slug?: string,
                options?: GetPostContentOptions,
                section?: string,
                serviceUrl?: string,
            ): Promise<WordPressPostData | undefined> {
                // Check in case this gets called directly without current context set
                if (!slug) {
                    return;
                }

                const existingContent = selectContentItemBySlug(get(), slug, section);
                // CmsData is already available or is loading, don't fetch
                if (existingContent?.data || existingContent?.isLoading) {
                    return;
                }

                const cacheKey = generateCacheKey(slug, get(), section);
                set(function updateContentItemForLoading(draft: WordPressState) {
                    draft.contentItemData[cacheKey] = {
                        isLoading: true,
                        isError: false,
                    };
                });
                try {
                    const content = await wordPressService.getPostContent(slug, options, section, serviceUrl);
                    set(function updateContent(draft: WordPressState) {
                        draft.contentItemData[cacheKey] = {
                            isLoading: false,
                            isError: false,
                            data: content ? trimAndCleanContentItem(content) : undefined,
                        };
                    });
                } catch (err) {
                    set(function updateContentForError(draft: WordPressState) {
                        draft.contentItemData[cacheKey] = {
                            isLoading: false,
                            isError: true,
                        };
                    });
                }
            },
            async fetchPersonas() {
                const currentState = get();
                // Only fetch if we don't have them cached already
                if (currentState.personas.length === 0) {
                    try {
                        const personas = await wordPressService.getPersonas(currentState.currentOptions.language);
                        set(function updatePersonas(draft: WordPressState) {
                            draft.personas = personas;
                            personas.forEach((persona) => {
                                const key = generateFilterKey(undefined, persona.slug);
                                if (key) {
                                    draft.orderedItemsByFilterKey[key] = {
                                        name: persona.name,
                                        slug: persona.slug,
                                        singular: persona?.labels?.singular || persona.name,
                                        plural: persona?.labels?.plural || persona.name,
                                        totalItems: persona.count,
                                        orderedItems: [],
                                        isLoading: false,
                                        isError: false,
                                    };
                                }
                            });
                            draft.personasOverallError = false;
                        });
                    } catch (err) {
                        set(function updateForContentTypesError(draft: WordPressState) {
                            draft.personasOverallError = true;
                        });
                    }
                }
            },
            reset() {
                set(function resetStore() {
                    return initialState;
                });
            },
        }),
        'wordPressStore',
        IS_DEVELOPMENT,
    );
}

export function generateCacheKey(
    slug: string,
    state: WordPressState = wordPressStore.getState(),
    section?: string,
): string {
    const queryType = state.currentOptions?.wordPressQueryType ?? 'post';
    const base = `${queryType}:${slug}`;
    if (section) {
        return `${base}:${section}`;
    }
    return base;
}

// export const generateCacheKey = (slug: string, queryType: WordPressQueryType = 'post'): string => {
//     return `${queryType}:${slug}`;
// };
